import mapboxgl, {
  GeoJSONSource,
  Marker,
  LngLat,
  GeoJSONSourceOptions,
  supported,
} from "mapbox-gl";
import {
  Action,
  CoverageType,
  Pharmacy,
  PharmacyGeoPoint,
  PharmacyGeoPoints,
  PlanType,
} from "../@types";
import {
  AnalyticsActionType,
  AnalyticsActions,
  Ga4Event,
  Ga4EventType,
  SearchResultType,
} from "../app/contexts/Analytics/types";
import { CONSTANTS } from "./CONSTANTS";
import { Position } from "geojson";

type CreateMarkerParams = {
  inactiveMapMarker: string;
  index: number;
  npi: string;
  name: string;
  page: number;
};

type MarkerOnClickParams = {
  cwCoverage: CoverageType | undefined;
  dispatch: React.Dispatch<Action | AnalyticsActions>;
  planType: PlanType | undefined;
  npi: string;
  totalResults: number;
};

export type MapLoadOrChangeHandlerParams = Omit<MarkerOnClickParams, "npi"> & {
  coordinates: Position;
  fallbackRadius: number | undefined;
  inactiveMapMarker: string;
  map: mapboxgl.Map;
  mapMarkers: mapboxgl.Marker[];
  onRadiusCache?: (radius: number) => void;
  page: number;
  pharmacies: PharmacyGeoPoints;
  pharmacyPointsRef: React.MutableRefObject<PharmacyGeoPoint[] | null>;
  setMapMarkers: React.Dispatch<React.SetStateAction<mapboxgl.Marker[]>>;
};

const metersToPixelsAtMaxZoom = (meters: number, latitude: number): number =>
  meters / 0.05 / Math.cos((latitude * Math.PI) / 180);

const markerOnClick = ({
  cwCoverage,
  dispatch,
  npi,
  planType,
  totalResults,
}: MarkerOnClickParams) => {
  const pharmacyListItem = document.getElementById(npi);
  const pharmacyNumber = pharmacyListItem?.querySelector(
    ".SelectPharmaciesPage__pharmacyNumber"
  )?.textContent;
  const mapClickText = `Map Click: Pharmacy #${
    (pharmacyNumber && pharmacyNumber[0]) || 0
  }`;
  pharmacyListItem?.scrollIntoView({ behavior: "smooth" });
  dispatch({
    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
    settings: {
      event_action: "Find Plans - Add Pharmacy",
      event_label: mapClickText,
    },
  });
  dispatch({
    type: AnalyticsActionType.SEND_GA4_EVENT,
    settings: {
      event_name: Ga4Event.PHARMACY_MAP_ENGAGEMENT,
      event_type: Ga4EventType.UI_INTERACTION,
      text: mapClickText,
      search_result_count: totalResults,
      search_result_type: SearchResultType.MAP_CLICK,
      mct_plan_type: planType,
      mct_medigap: false,
      mct_coverage_wizard: !!cwCoverage,
    },
  });
};

export const createMarker = ({
  inactiveMapMarker,
  index,
  name,
  npi,
  page,
}: CreateMarkerParams) => {
  const markerElement = document.createElement("div");
  markerElement.textContent = `${
    page * CONSTANTS.pharmacySelection.pageCount + (index + 1)
  }`;
  markerElement.className = "PharmacySelectionMap__marker";
  markerElement.setAttribute("data-npi", npi);
  markerElement.setAttribute("data-testid", npi);
  markerElement.setAttribute("title", name);
  markerElement.style.backgroundImage = `url(${inactiveMapMarker})`;
  return markerElement;
};

/**
 * @TODO - Break out into smaller functoins, address exhaustive deps in component
 */
export const mapLoadOrChangeHandler = ({
  coordinates,
  cwCoverage,
  dispatch,
  fallbackRadius,
  inactiveMapMarker,
  map,
  mapMarkers,
  onRadiusCache,
  page,
  pharmacies,
  pharmacyPointsRef,
  planType,
  setMapMarkers,
  totalResults,
}: MapLoadOrChangeHandlerParams) => {
  // remove existing markers
  mapMarkers.forEach(marker => marker.remove());

  Array.from(
    document.querySelectorAll(".mapboxgl-control-container a")
  ).forEach(a => {
    a.setAttribute("tabindex", "-1");
  });

  const { features: pharmacyPoints } = pharmacies;
  const [sourceLng, sourceLat] = coordinates;

  pharmacyPointsRef.current = pharmacyPoints;

  let distanceInPixels = fallbackRadius || 0;

  if (pharmacyPoints.length) {
    const pharmaciesSource =
      (map.getSource("pharmacies") as GeoJSONSource) || undefined;
    if (pharmaciesSource) {
      pharmaciesSource.setData(pharmacies);
    } else {
      map.addSource("pharmacies", {
        type: "geojson",
        data: pharmacies,
      });
    }

    const lats: number[] = [];
    const longs: number[] = [];
    const markers: Marker[] = [];
    let farthestPharmacy = { lng: 0, lat: 0, distance_miles: 0 };

    pharmacyPoints.forEach((feature, index) => {
      const { geometry, properties } = feature;
      const { coordinates } = geometry as { coordinates: number[] };
      const { npi, distance_miles = 0, name } = properties as Pharmacy;

      longs.push(coordinates[0]);
      lats.push(coordinates[1]);

      if (distance_miles > farthestPharmacy.distance_miles) {
        farthestPharmacy = {
          lng: coordinates[0],
          lat: coordinates[1],
          distance_miles,
        };
      }

      const markerElement = createMarker({
        inactiveMapMarker,
        index,
        name,
        npi,
        page,
      });
      markerElement.onclick = () => {
        markerOnClick({ cwCoverage, dispatch, npi, planType, totalResults });
      };
      const marker = new Marker(markerElement)
        .setLngLat(new LngLat(coordinates[0], coordinates[1]))
        .addTo(map);
      markers.push(marker);
    });

    setMapMarkers(markers);

    if (lats.length && longs.length) {
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLong = Math.min(...longs);
      const maxLong = Math.max(...longs);

      map.fitBounds(
        [
          [minLong - 0.01, minLat - 0.01],
          [maxLong + 0.01, maxLat + 0.01],
        ],
        { animate: false }
      );

      const srcLngLat = new LngLat(sourceLng, sourceLat);
      const farthestLngLat = new LngLat(
        farthestPharmacy.lng,
        farthestPharmacy.lat
      );

      const distanceBetween = srcLngLat.distanceTo(farthestLngLat);

      distanceInPixels = metersToPixelsAtMaxZoom(
        distanceBetween,
        farthestPharmacy.lat
      );

      if (onRadiusCache) {
        onRadiusCache(distanceInPixels);
      }
    }
  }

  const circleLayerData: GeoJSONSourceOptions["data"] = {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [sourceLng, sourceLat],
    },
    properties: {},
  };

  const circleRadiusData = {
    stops: [
      [0, 0],
      [20, distanceInPixels],
    ],
    base: 2,
  };

  const circleSource = map.getSource("pharmacy-circle-source") as GeoJSONSource;
  const circleLayer = map.getLayer("pharmacy-circle");

  if (circleSource && circleLayer) {
    const circleSource = map.getSource(
      "pharmacy-circle-source"
    ) as GeoJSONSource;
    circleSource.setData(circleLayerData);
    map.setPaintProperty("pharmacy-circle", "circle-radius", circleRadiusData);
  } else {
    map.addSource("pharmacy-circle-source", {
      type: "geojson",
      data: circleLayerData,
    });
    map.addLayer({
      id: "pharmacy-circle",
      source: "pharmacy-circle-source",
      type: "circle",
      paint: {
        "circle-radius": circleRadiusData,
        "circle-color": "#146A5D",
        "circle-stroke-color": "#146A5D",
        "circle-stroke-width": 1,
        "circle-opacity": 0.1,
        "circle-pitch-scale": "viewport",
      },
    });
  }

  map.resize();
};

export const updateMapMarkers = ({
  mapMarkers,
  selectedPharmacies,
}: {
  mapMarkers: mapboxgl.Marker[];
  selectedPharmacies: Pharmacy[];
}): void => {
  mapMarkers.forEach(marker => {
    const element = marker.getElement();
    const npi = element.getAttribute("data-npi");
    if (npi && selectedPharmacies.map(p => p.npi).includes(npi)) {
      element.classList.add("PharmacySelectionMap__marker--active");
    } else {
      element.classList.remove("PharmacySelectionMap__marker--active");
    }
  });
};

export const mapboxIsSupported = supported({
  failIfMajorPerformanceCaveat: true,
});
